// @ts-strict-ignore
import { dayjs } from '@/extendedDayjs';
import * as React from 'react';
import { useSelector } from 'react-redux';
import {
    selectActivePilotForRelease,
    selectAppHasSelectCapabilitiesConfigured,
    selectAppTimeFormatSetting,
    selectCLIUsers,
    selectEffectiveTimezone,
    selectUserById,
    selectUserCanPerformAction,
} from '../selectors/selectors';
import { displayUserNameShort } from '../helpers/displayHelpers';
import { RootState } from '../reducers/reducers';
import {
    ChecklistItem,
    ChecklistItemStatusType,
    DefaultUserGroupId,
    IntegrationCapability,
    OrgUser,
    RelativeTimeTrigger,
    UserGroup,
    UserGroupId,
} from '../types';
import { antGrey, getAvatarColorForName, runwayColors, tagStyle } from './styles/globalStyles';
import Avatar from 'antd/lib/avatar';
import Tooltip from 'antd/lib/tooltip';
import Text from 'antd/lib/typography/Text';
import Space from 'antd/lib/space';
import Button from 'antd/lib/button';
import Paragraph from 'antd/lib/typography/Paragraph';
import { formatDateWithTimezone } from '../timeHelpers';
import { RunwayUserAction } from '../helpers/aclHelpers';
import Checkbox from 'antd/lib/checkbox';
import { Nullable, ReleaseComponentProps, StepRunComponentProps } from '../typescriptHelpers';
import Tag from 'antd/lib/tag';
import { OwnerAvatar, OwnerAvatarGroup, ReleasePilotAvatar } from './Avatars';
import PingButton from './PingButton';
import GroupTooltip from './checklistItems/GroupTooltip';
import Divider from 'antd/lib/divider';
import { TargetCompletionDueView } from '@/components/ChecklistItemTargetTime';
import { ApartmentOutlined } from '@ant-design/icons';
import ChecklistItemCommentButton from './checklistItems/ChecklistItemCommentButton';
import { Link } from 'react-router-dom';
import { createOrgSettingsRoute } from '../helpers/routeHelpers';
import { OrgSettingsTabType } from '@/typescriptHelpers';
import TimeWithConditionalTz from './common/TimeWithConditionalTz';

interface ChecklistItemRowComponentProps extends ChecklistItem, ReleaseComponentProps {
    isEditing: boolean;
    showAsComplete: boolean;
    onClickInfo: (id: string, title: string, description?: string) => void;
    itemStatusChanged: (id: string, status?: ChecklistItemStatusType) => void;
    onAddComment: (id: string) => void;
    isDisabled: boolean;
    orgGroups: Record<UserGroupId, UserGroup>;
    stepRunComponentProps: Nullable<StepRunComponentProps>;
    dueDate: Nullable<string>;
    targetCompletion?: RelativeTimeTrigger;
    shouldDisplayInReadOnlyMode?: boolean;
}

export const orgLevelCLIIcon = (orgId: string) => {
    return (
        <Tooltip
            title={
                <>
                    {' '}
                    This is an organization-level checklist item that may affect multiple workflows. Visit{' '}
                    <Link
                        to={createOrgSettingsRoute(orgId, OrgSettingsTabType.Checklists)}
                        style={{ color: runwayColors.white, textDecoration: 'underline' }}
                    >
                        Organization settings
                    </Link>{' '}
                    to make changes.
                </>
            }
        >
            <ApartmentOutlined style={{ marginRight: 8, color: antGrey.grey6 }} />
        </Tooltip>
    );
};

const ChecklistItemRowComponent: React.FC<ChecklistItemRowComponentProps> = (props: ChecklistItemRowComponentProps) => {
    const { orgId, appId, releaseId, isEditing, onClickInfo, showAsComplete, isDisabled } = props;
    const checkboxRef = React.useRef(null);
    const cliOwnerUsers = useSelector((state: RootState) => selectCLIUsers(state, orgId, appId, props.owners));
    const hasCommsIntegrationConnected = useSelector((state: RootState) =>
        selectAppHasSelectCapabilitiesConfigured(state, appId, [IntegrationCapability.Notifications]),
    );

    const hasIndividualOwners = props.owners?.length > 0;
    const hasOneOwner = props.owners?.length === 1;
    const hasMultipleOwners = props.owners?.length > 1;

    const activeReleasePilotUser = useSelector((state: RootState) =>
        selectActivePilotForRelease(state, orgId, releaseId),
    );
    const ownedByPilot =
        hasOneOwner &&
        activeReleasePilotUser?.id === props.owners[0] &&
        props.approverGroups.length === 1 &&
        props.approverGroups[0] === DefaultUserGroupId.Pilot;

    const userCanToggleCLI = useSelector((state: RootState) =>
        selectUserCanPerformAction(state, RunwayUserAction.UAToggleApprovalCLI, props.releaseId),
    );

    const approvedByUser = useSelector((state: RootState) => {
        if (props.status) {
            return selectUserById(state, orgId, appId, props.status.createdBy);
        }

        return null;
    });

    const use24HourTimeFormat = useSelector((state: RootState) => selectAppTimeFormatSetting(state, props.appId));
    const effectiveTimezone = useSelector((state: RootState) => selectEffectiveTimezone(state, props.appId));

    const avatar = (displayName: string) => (
        <Avatar
            size="small"
            src={approvedByUser.commsAvatars?.image32}
            style={{
                backgroundColor: getAvatarColorForName(displayName),
                opacity: opacity,
            }}
        >
            {displayName.charAt(0).toUpperCase()}
        </Avatar>
    );

    const approvedByUserAvatar = (user: OrgUser) => {
        const displayName = displayUserNameShort(user);
        return isDisabled ?
                <>{avatar(displayName)}</>
            :   <Tooltip title={displayName} placement="topRight">
                    <>{avatar(displayName)}</>
                </Tooltip>;
    };

    const textColor = isDisabled ? antGrey.grey6 : antGrey.grey8;
    const opacity = isDisabled ? '0.3' : '1.0';
    const baseStyle: React.CSSProperties = { paddingLeft: 12 };

    const checkbox =
        userCanToggleCLI ?
            <Checkbox
                style={baseStyle}
                checked={props.showAsComplete}
                onChange={() => {
                    const isChecked = !!props.status;
                    const updatedCheckedStatus = !isChecked;
                    props.itemStatusChanged(
                        props.id,
                        updatedCheckedStatus === true ? ChecklistItemStatusType.Approved : null,
                    );

                    checkboxRef.current.blur();
                }}
                ref={checkboxRef}
                disabled={isDisabled}
            />
        :   <Tooltip title="You don't have permission to perform this action. Please contact your organization's admin.">
                <Checkbox
                    disabled
                    style={baseStyle}
                    checked={props.showAsComplete}
                    onChange={() => {
                        const isChecked = !!props.status;
                        const updatedCheckedStatus = !isChecked;
                        props.itemStatusChanged(
                            props.id,
                            updatedCheckedStatus === true ? ChecklistItemStatusType.Approved : null,
                        );
                    }}
                />
            </Tooltip>;

    const maxTitleLength = isEditing ? 65 : 80;
    const displayTitle = props.title.length > maxTitleLength ? `${props.title.slice(0, maxTitleLength)}…` : props.title;

    const nowrapStyle: React.CSSProperties = { whiteSpace: 'nowrap' };

    return (
        <div
            style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                minWidth: 0,
                gap: 16,
                width: '100%',
                overflow: 'hidden',
            }}
        >
            <Space style={{ minWidth: 0, flex: '1 1 0%', overflow: 'hidden' }}>
                <Space style={{ minWidth: 0, overflow: 'hidden' }}>
                    {isEditing ? null : checkbox}
                    <Tooltip title={props.title.length > maxTitleLength ? props.title : undefined}>
                        <div
                            style={{
                                paddingRight: 8,
                                paddingLeft: 8,
                                minWidth: 0,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            <Paragraph
                                style={{
                                    marginBottom: 0,
                                    textDecoration: showAsComplete ? 'line-through' : 'none',
                                    color: textColor,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {!!props.orgTemplateId && orgLevelCLIIcon(props.orgId)}
                                {displayTitle}
                            </Paragraph>
                        </div>
                    </Tooltip>
                    {(props.description || props.attachments?.length > 0) && (
                        <Button
                            onClick={() => onClickInfo(props.id, props.title, props.description)}
                            size="small"
                            shape="round"
                            disabled={isDisabled}
                        >
                            Info
                        </Button>
                    )}
                </Space>
                {props.oneOffForVersion && (
                    <Tag
                        color={isDisabled ? antGrey.grey4 : antGrey.grey6}
                        style={{ ...tagStyle, lineHeight: '10px', padding: '2px 4px', flexShrink: 0 }}
                    >
                        {props.oneOffForVersion}
                    </Tag>
                )}
            </Space>

            <Space
                direction="horizontal"
                size="middle"
                style={{ flexShrink: 0, flexWrap: 'nowrap', alignItems: 'center' }}
            >
                {props.targetCompletion?.enabled && !props.showAsComplete && !!props.dueDate && (
                    <div style={{ display: 'flex', columnGap: 16, ...nowrapStyle }}>
                        <TargetCompletionDueView isDisabled={isDisabled} dueDate={props.dueDate} />
                        <Divider type="vertical" style={{ height: 'auto', alignSelf: 'stretch', margin: 0 }} />
                    </div>
                )}

                {props.showAsComplete && (
                    <Space size="middle" style={{ display: 'flex', flexWrap: 'nowrap', ...nowrapStyle }}>
                        <Text
                            style={{
                                color: runwayColors.statusGreen,
                                opacity: opacity,
                            }}
                        >
                            Completed
                        </Text>
                        {props.status?.createdAt && (
                            <div style={{ display: 'flex', columnGap: 16, ...nowrapStyle }}>
                                <Divider type="vertical" style={{ height: 'auto', alignSelf: 'stretch', margin: 0 }} />
                                <Text type="secondary" style={{ opacity: opacity, ...nowrapStyle }}>
                                    <TimeWithConditionalTz
                                        triggeredAtLabel={formatDateWithTimezone(
                                            props.status?.createdAt,
                                            'short',
                                            effectiveTimezone,
                                            use24HourTimeFormat,
                                        )}
                                        triggeredByMoment={dayjs(props.status?.createdAt)}
                                        use24HourTimeFormat={use24HourTimeFormat}
                                        effectiveTimezone={effectiveTimezone}
                                        style={{ color: 'inherit', opacity: opacity, ...nowrapStyle }}
                                    />
                                </Text>
                            </div>
                        )}
                    </Space>
                )}

                {props.showAsComplete && approvedByUser && approvedByUserAvatar(approvedByUser)}
                {!props.showAsComplete &&
                    props.approverGroups &&
                    props.approverGroups.length > 0 &&
                    !hasIndividualOwners && (
                        <>
                            <Text type="secondary" style={{ fontSize: 14, opacity: opacity, ...nowrapStyle }}>
                                owned by
                            </Text>
                            {props.approverGroups.map((groupId, index) => {
                                const omitComma = index === props.approverGroups.length - 1;
                                const group = props.orgGroups[groupId];
                                return (
                                    <div key={groupId} style={nowrapStyle}>
                                        <GroupTooltip
                                            orgId={props.orgId}
                                            appId={props.appId}
                                            releaseId={props.releaseId}
                                            group={group}
                                            showComma={!omitComma}
                                            isDisabled={isDisabled}
                                        />
                                    </div>
                                );
                            })}
                        </>
                    )}

                {!props.showAsComplete && hasIndividualOwners && cliOwnerUsers[0] && (
                    <>
                        <Text type="secondary" style={{ fontSize: 14, opacity: opacity, ...nowrapStyle }}>
                            owned by
                        </Text>
                        {hasOneOwner && (
                            <Space size="small" style={{ display: 'flex', flexWrap: 'nowrap', ...nowrapStyle }}>
                                <OwnerAvatar user={cliOwnerUsers[0]} isDisabled={isDisabled} />
                                <Text style={{ color: textColor, ...nowrapStyle }}>
                                    {displayUserNameShort(cliOwnerUsers[0])}
                                </Text>
                                {ownedByPilot && <ReleasePilotAvatar isDisabled={isDisabled} />}
                            </Space>
                        )}
                        {hasMultipleOwners && (
                            <OwnerAvatarGroup maxAvatars={3} owners={cliOwnerUsers} isDisabled={isDisabled} />
                        )}
                    </>
                )}

                <ChecklistItemCommentButton
                    id={props.id}
                    comment={props.comment}
                    isDisabled={isDisabled}
                    onAddComment={props.onAddComment}
                    effectiveTimezone={effectiveTimezone}
                    use24HourTimeFormat={use24HourTimeFormat}
                    orgId={orgId}
                    appId={appId}
                />

                {!isEditing && !props.shouldDisplayInReadOnlyMode && (
                    <div>
                        <PingButton
                            orgId={orgId}
                            appId={appId}
                            releaseId={releaseId}
                            checklistItemId={props.id}
                            text={`${hasMultipleOwners ? 'Ping all' : 'Ping'}`}
                            disabled={props.showAsComplete || !hasCommsIntegrationConnected}
                            disablePopover={isDisabled}
                            missingIntegration={!hasCommsIntegrationConnected}
                            stepRunComponentProps={props.stepRunComponentProps}
                        />
                    </div>
                )}
            </Space>
        </div>
    );
};

export default ChecklistItemRowComponent;
